// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import { formatGlucoseForExport } from '../common'

/**
 * Record that a clinical record left the portal (ada-core `export-events`).
 *
 * WHY
 * A CSV/PDF export hands a patient's CGM history to someone outside the
 * system. Until 2026-08-29 that left no server-side trace, so "who gave the
 * owner that report?" was unanswerable. This posts the METADATA only — who,
 * which pet, what window, what format, how many rows, how many of them masked.
 * No glucose values leave in the audit row: provenance does not need a second
 * copy of the PHI.
 *
 * ⚠️ FAIL-OPEN, deliberately.
 * If the audit POST fails the export still proceeds. The alternative — refusing
 * a vet their patient's report because a logging call 500'd — trades a clinical
 * workflow for a bookkeeping guarantee, which is the wrong way round. The cost
 * is that the trail can have gaps under backend failure, so it is evidence of
 * what WAS exported, never proof of everything that was. Anyone reasoning about
 * completeness has to know that; it is why this comment exists rather than a
 * silent `.catch(() => {})`.
 *
 * `vet` and `origin` are intentionally NOT sent: ada-core forces both from the
 * authenticated user (ExportEvents beforeValidate), so a client cannot
 * attribute an export to someone else, nor launder one by claiming a different
 * provenance.
 *
 * ⚠️ Do NOT add an `origin` field here, however reasonable it looks (vic/vetpo#3).
 * The point of `origin` is separating the beta smoke suite's traffic from real
 * vet exports — and the smoke suite drives the REAL deployed production bundle
 * through Playwright, so this exact code runs in both cases. A client-set
 * origin therefore cannot distinguish them EVEN IN PRINCIPLE, while adding a
 * spoofable provenance claim to an audit trail. The account is the only signal,
 * and only the server can see it.
 */
export interface ExportEventInput {
  petId: number | string
  deviceId?: number | string | null
  format: 'csv' | 'pdf'
  unit?: string
  rangeStart?: Date | null
  rangeEnd?: Date | null
  rowCount?: number
  maskedCount?: number
  /**
   * Was the exported window capped by the server limit? Tri-state on the wire:
   * omit it and the column stays NULL, meaning "this client did not report".
   * Never coerce an unknown to `false` — that asserts a completeness nobody
   * checked, which is the whole failure this field exists to remove.
   */
  truncated?: boolean
  /**
   * The sensors the vet selected in the export dialog (vic/vetpo#56), and how:
   * one sensor, several, or a custom date range. Lets ops see how vets use the
   * device picker. ⚠️ ada-core ExportEvents has no columns for these yet
   * (checked 2026-09-26): Payload drops unknown keys, so they are sent but not
   * stored until the backend adds `devices` + `selectionMode`.
   */
  devices?: ReadonlyArray<number | string>
  selectionMode?: 'single' | 'multi' | 'custom'
}

const apiUrl = import.meta.env.VITE_BACKEND_API_URL || '/api'

/**
 * Count readings that export as a MASKED value ("<40" / ">540" / "<2.2" / ">30")
 * rather than a number. This is the one figure that makes an export
 * reconcilable after the fact — two exports of the same window can mask
 * different amounts (VP-EXPORT R08-R14), so without it a disputed report
 * cannot be checked against the data.
 */
export const countMaskedReadings = (
  metrics: ReadonlyArray<{ glucose?: number | null }>,
  unit: string,
): number =>
  metrics.reduce((n, m) => {
    if (typeof m?.glucose !== 'number') return n
    const cell = formatGlucoseForExport(m.glucose, unit)
    return cell.startsWith('<') || cell.startsWith('>') ? n + 1 : n
  }, 0)

export const recordExportEvent = (input: ExportEventInput): void => {
  const token = sessionStorage.getItem('token')
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `JWT ${token}`

  const petId = Number(input.petId)
  if (!Number.isFinite(petId)) return

  // A virtual "All Devices" selection is not a real device id — send null
  // rather than coercing a synthetic id into the audit row.
  const deviceRaw = Number(input.deviceId)
  const device = Number.isInteger(deviceRaw) && deviceRaw > 0 ? deviceRaw : null

  void fetch(`${apiUrl}/export-events`, {
    method: 'POST',
    headers,
    credentials: 'include', // cookie-only sessions must audit too (VP-AUTH-R12)
    body: JSON.stringify({
      pet: petId,
      format: input.format,
      unit: input.unit,
      device,
      rangeStart: input.rangeStart ? input.rangeStart.toISOString() : null,
      rangeEnd: input.rangeEnd ? input.rangeEnd.toISOString() : null,
      rowCount: input.rowCount,
      maskedCount: input.maskedCount,
      truncated: input.truncated,
      devices: input.devices?.map((d) => Number(d)).filter((d) => Number.isInteger(d) && d > 0),
      selectionMode: input.selectionMode,
    }),
  }).catch((error) => {
    // Fail-open (see above) but never silent — a vanished trail must be
    // diagnosable, and a bare `.catch(() => {})` is how it stops being.
    console.warn('export audit not recorded:', error)
  })
}
